import { Button, Card, CircularProgress, Typography } from '@mui/material';
import { Form, Formik, Field } from 'formik';
import { TextField } from 'formik-mui';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { userEdit, userDelete, registerLocal, userGet } from '../api/api';

import Box from '@mui/material/Box';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';
import MuiPhoneNumber from 'material-ui-phone-number-2';
import phone from 'phone';
import { useProfile } from 'api/hooks';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useSnackbar } from 'store/store';
import { omit } from 'lodash';
import { useMedia } from 'react-use';

export const UserEdit = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { profile, isLoading } = useProfile();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const isDarkTheme = useMedia('(prefers-color-scheme: dark)');

  const snackbar = useSnackbar();
  const alert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const handleClickOpen = () => {
    setConfirmOpen(true);
  };

  const handleClose = () => {
    setConfirmOpen(false);
  };

  const hasPathParam = !!userId;

  const isSuperAdmin =
    profile?.roles?.some((role) => role.type === 'SUPER_ADMIN') ?? false;
  const isAdmin =
    (profile?.roles?.some((role) => role.type === 'ADMIN') || isSuperAdmin) ??
    false;

  const {
    mutateAsync,
    error: mutateError,
    data: mutateData,
    isLoading: mutateIsLoading,
  } = useMutation('userEdit', hasPathParam ? userEdit : registerLocal, {
    onSuccess: () => {
      navigate('/');
    },
    onError: (err: any) => {
      alert('Error', err.message);
    },
  });

  const { data } = useQuery('getUserProfile', userGet(userId!), {
    enabled: !!userId,
    suspense: true,
  });

  const user = data?.data;

  const handlePhoneChange = useCallback(
    (originalHandler) => (value) => {
      originalHandler({ target: { name: 'phone', value } });
    },
    [],
  );

  return (
    <div className="flex flex-col gap-2 justify-center items-center w-full">
      <>
        <Dialog
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{`${alertTitle}`}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {`${alertMessage}`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAlertOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>
        {isLoading && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {(!hasPathParam || user) && (
          <>
            <Dialog
              open={confirmOpen}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {'Deletion confirmation'}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  {`The user will not be deleted if it is the owner of an organization. Are you sure you want to delete this?`}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                <Button
                  onClick={async () => {
                    try {
                      const response = await userDelete(userId!)();
                      setConfirmOpen(false);
                      if (response) navigate('/');
                    } catch (error: any) {
                      setConfirmOpen(false);
                      alert(error.error, error.message);
                    }
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
            <Card
              variant="outlined"
              sx={{
                width: '100%',
                maxWidth: { xs: '450px', md: '750px' },
                borderWidth: isDarkTheme ? '0px' : '1px',
                padding: { xs: '16px 16px 24px', sm: '48px' },
                margin: { xs: '16px auto 0', md: '48px auto 8px' },
                display: 'flex',
                flexDirection: 'row',
                gap: '48px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  position: 'relative',
                  flexGrow: 1,
                }}
              >
                <Formik
                  validateOnMount={true}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  initialValues={
                    user || {
                      givenName: '',
                      familyName: '',
                      email: '',
                      password: '',
                      confirmPassword: '',
                      phone: '',
                    }
                  }
                  validationSchema={Yup.object().shape({
                    givenName: Yup.string()
                      .min(2, 'Too short')
                      .max(50, 'Too long')
                      .required('Required'),
                    familyName: Yup.string()
                      .min(2, 'Too short')
                      .max(50, 'Too long')
                      .required('Required'),
                    email: Yup.string()
                      .email('Invalid email')
                      .required('Required'),
                    phone: Yup.string()
                      .test('phone', 'Invalid phone number', (value) => {
                        if (!value) return true;
                        return phone(value).isValid;
                      })
                      .required('Required'),
                    password: Yup.string()
                      .min(8, 'Too short')
                      .max(50, 'Too long')
                      .required('Required'),
                    confirmPassword: Yup.string().oneOf(
                      [Yup.ref('password'), null],
                      'Those passwords didn’t match. Try again.',
                    ),
                  })}
                  onSubmit={async (values, { setSubmitting }) => {
                    //console.log(values);
                    try {
                      const res = await mutateAsync({
                        values: omit(values, ['confirmPassword']),
                      });
                      const verify = res.data.verify;
                      if (verify) {
                        snackbar.open({
                          type: 'success',
                          message: `Verification email was sent to ${values.email}`,
                        });
                      } else {
                        queryClient.invalidateQueries('profile');
                      }
                    } catch (error: any) {
                      alert(error.error, error.message);
                    }
                    setSubmitting(false);
                  }}
                >
                  {({
                    submitForm,
                    isSubmitting,
                    values,
                    isValid,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                  }) => (
                    <Form style={{ width: '100%' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <Typography
                          variant="h4"
                          component="h2"
                          sx={{
                            textAlign: { xs: 'center', md: 'left' },
                            margin: { xs: '4px 0', sm: '0 0 24px' },
                            fontSize: { xs: '1.55em', sm: '1.8rem' },
                          }}
                        >
                          {!!hasPathParam ? 'Edit Account' : 'Create Account'}
                        </Typography>
                        <Field
                          component={TextField}
                          label="Given name"
                          name="givenName"
                          size="small"
                        />
                        <Field
                          component={TextField}
                          label="Family name"
                          name="familyName"
                          size="small"
                        />
                        <Field
                          component={TextField}
                          label="Email"
                          name="email"
                          size="small"
                        />
                        <Field
                          type="password"
                          component={TextField}
                          label="Password"
                          name="password"
                          size="small"
                        />
                        <Field
                          type="password"
                          component={TextField}
                          label="Confirm password"
                          name="confirmPassword"
                          size="small"
                        />
                        <MuiPhoneNumber
                          required
                          size="small"
                          defaultCountry={'rw'}
                          name="phone"
                          label="Phone"
                          variant="outlined"
                          value={values.phone}
                          onBlur={handleBlur}
                          onChange={handlePhoneChange(handleChange)}
                          error={touched.phone && !!errors.phone}
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          //@ts-ignore
                          helperText={touched.phone && errors.phone}
                          className="MuiPhoneNumber"
                          InputLabelProps={{ required: false }}
                        />
                        <Box
                          sx={{
                            textAlign: 'right',
                            margin: '16px 0',
                            display: 'flex',
                            flexDirection: 'row',
                          }}
                        >
                          <Button
                            color="primary"
                            disabled={isSubmitting}
                            onClick={() => {
                              navigate('/');
                            }}
                            sx={{ padding: '6px 8px' }}
                          >
                            Cancel
                          </Button>
                          <Box sx={{ flex: '1 1 auto' }} />
                          {hasPathParam && (
                            <Button
                              color="primary"
                              disabled={isSubmitting}
                              onClick={handleClickOpen}
                              sx={{ padding: '6px 8px' }}
                            >
                              Delete
                            </Button>
                          )}
                          <Box sx={{ flex: '1 1 auto' }} />
                          <Button
                            color="primary"
                            variant="contained"
                            disabled={isSubmitting || !isValid}
                            onClick={submitForm}
                            sx={{ padding: '6px 24px' }}
                          >
                            {!!hasPathParam ? 'Save' : 'Register'}
                          </Button>
                        </Box>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </Box>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Box
                  component="img"
                  sx={{
                    height: 244,
                    width: 244,
                  }}
                  alt="Sign up"
                  src="/account.svg"
                />
                <Box
                  sx={{
                    textAlign: 'center',
                    fontWeight: '300',
                    opacity: 0.95,
                  }}
                >
                  All your tickets
                  <br />
                  in one place
                </Box>
              </Box>
            </Card>
          </>
        )}
      </>
    </div>
  );
};
